import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { Box, Button, FormHelperText, TextField } from '@mui/material';
import { ApiError, AsyncAutocomplete } from '@pro4-erp/core-web';
import { useQuery } from '@tanstack/react-query';
import {
  AuditorCategory,
  Classification,
  Standard,
  getStandards,
  getClassifications,
  getAuditorCategories,
  CodeList,
} from '@/common';
import ConfirmModal from './ConfirmModal';
import { AuditProposalsStatementStatement } from '../types';
import { getAuditProposalsStatement } from '../api/getAuditProposalsStatement';

type FormType = {
  note: string;
  workExperience: string;
  education: string;
  training: string;
  workplaceTraining: string;
  statusNote: string;
  status: CodeList | null;
  standard: Standard | null;
  class: Classification | null;
  auditorCategory: AuditorCategory | null;
};

type FormProps = {
  onSubmit: (data: Partial<FormType>) => void;
  initialState?: FormType;
  hasFormChanged?: (form: FormType) => boolean;
  onUnmount?: (form: FormType) => void;
  isFormEditable?: boolean;
};

const Form = ({ onSubmit, initialState, hasFormChanged, onUnmount, isFormEditable }: FormProps) => {
  const { t, i18n } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const lang = i18n.language;
  const query = useQuery<AuditProposalsStatementStatement, ApiError>(
    ['audit-proposals-statement', lang],
    () => getAuditProposalsStatement(lang),
    { refetchOnWindowFocus: false }
  );
  const form = useForm<FormType>({
    mode: 'onTouched',
    defaultValues: {
      note: '',
      workExperience: '',
      education: '',
      training: '',
      workplaceTraining: '',
      statusNote: '',
      status: null,
      standard: null,
      class: null,
      auditorCategory: null,
    },
  });

  useEffect(() => {
    return () => {
      if (onUnmount) {
        onUnmount(form.getValues());
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    form.reset({
      note: initialState?.note,
      workExperience: initialState?.workExperience,
      education: initialState?.education,
      training: initialState?.training,
      workplaceTraining: initialState?.workplaceTraining,
      standard: initialState?.standard,
      class: initialState?.class,
      statusNote: initialState?.statusNote,
      status: initialState?.status,
      auditorCategory: initialState?.auditorCategory,
    });
    query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState, form]);

  const baseSubmit = (data: FormType) => {
    if (!data.standard) {
      form.setError('standard', {
        message: t('Standard is required.', {
          defaultValue: 'Standard is required. ',
        }).toString(),
      });
      enqueueSnackbar(t('Standard is required.', { defaultValue: 'Standard is required.' }), {
        variant: 'warning',
      });
      return;
    }
    if (hasFormChanged) {
      if (!hasFormChanged(data)) {
        enqueueSnackbar(t('Form was not changed.', { defaultValue: 'Form was not changed.' }), {
          variant: 'info',
        });
        return;
      }
    }

    onSubmit(data);
  };

  const getClassificationName = (classification: FormType['class']): string => {
    if (classification?.name && classification?.code) {
      return `${classification?.code} ${classification.name}`;
    }
    if (classification?.name) {
      return classification?.name;
    }
    return 'N/A';
  };

  return (
    <Box component="form" onSubmit={form.handleSubmit(baseSubmit)} noValidate>
      <ConfirmModal
        isOpen={isModalOpen}
        handleClose={() => {
          enqueueSnackbar(
            t('The template cannot be created without statement confirmation', {
              defaultValue: 'The template cannot be created without statement confirmation',
            }),
            {
              variant: 'warning',
            }
          );
          setIsModalOpen(false);
        }}
        onConfirm={() => {
          form.handleSubmit(baseSubmit)();
          setIsModalOpen(false);
        }}
        osvStatement={query?.data?.Note || null}
      />
      {form.getValues('statusNote') && (
        <Box sx={{ mb: '2rem' }}>
          <Controller
            control={form.control}
            name="statusNote"
            render={({ field }) => {
              if (field.value) {
                return (
                  <TextField
                    {...field}
                    label={t('Status note', { defaultValue: 'Status note' })}
                    multiline
                    fullWidth
                    inputProps={{ disabled: true }}
                    color="error"
                    focused
                    error={!!form.formState.errors.statusNote}
                    helperText={form.formState.errors.statusNote?.message?.toString()}
                  />
                );
              }
              return <> </>;
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <Controller
          control={form.control}
          name="note"
          rules={{
            required: t('Note is required.', { defaultValue: 'Note is required.' }).toString(),
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('Note', { defaultValue: 'Note' })}
              fullWidth
              multiline
              disabled={!isFormEditable}
              error={!!form.formState.errors.note}
              helperText={form.formState.errors.note?.message?.toString()}
            />
          )}
        />

        <Controller
          control={form.control}
          name="workExperience"
          render={({ field }) => (
            <TextField
              {...field}
              label={t('Work experience', { defaultValue: 'Work experience' })}
              fullWidth
              multiline
              disabled={!isFormEditable}
              error={!!form.formState.errors.workExperience}
              helperText={form.formState.errors.workExperience?.message?.toString()}
            />
          )}
        />

        <Controller
          control={form.control}
          name="education"
          render={({ field }) => (
            <TextField
              {...field}
              label={t('Education', { defaultValue: 'Education' })}
              fullWidth
              multiline
              disabled={!isFormEditable}
              error={!!form.formState.errors.education}
              helperText={form.formState.errors.education?.message?.toString()}
            />
          )}
        />

        <Controller
          control={form.control}
          name="training"
          render={({ field }) => (
            <TextField
              {...field}
              label={t('Training', { defaultValue: 'Training' })}
              fullWidth
              multiline
              disabled={!isFormEditable}
              error={!!form.formState.errors.training}
              helperText={form.formState.errors.training?.message?.toString()}
            />
          )}
        />

        <Controller
          control={form.control}
          name="workplaceTraining"
          render={({ field }) => (
            <TextField
              {...field}
              label={t('Workplace training', { defaultValue: 'Workplace training' })}
              fullWidth
              multiline
              disabled={!isFormEditable}
              error={!!form.formState.errors.workplaceTraining}
              helperText={form.formState.errors.workplaceTraining?.message?.toString()}
            />
          )}
        />

        <AsyncAutocomplete<Standard>
          id="standard"
          label={t('Standard', { defaultValue: 'Standard' })}
          fullWidth
          disabled={!isFormEditable}
          getData={async (search) => {
            const response = await getStandards({
              page: 1,
              pageSize: 1000,
              search,
            });

            return response.results;
          }}
          getOptionLabel={(option) => option.reference || 'N/A'}
          isOptionEqualToValue={(option, value) => option.id === (!value ? undefined : value.id)}
          selectedOption={form.getValues().standard}
          setSelectedOption={(option) => {
            form.clearErrors('standard');

            if (option === null) {
              form.setError('standard', {
                message: t('Standard is required.', {
                  defaultValue: 'Standard is required. ',
                }).toString(),
              });
            }

            form.setValue('standard', option, { shouldValidate: true, shouldDirty: true });
          }}
        />

        <AsyncAutocomplete<Classification>
          id="class"
          label={t('Classification', { defaultValue: 'Classification' })}
          fullWidth
          disabled={!isFormEditable}
          getData={async (search) => {
            const response = await getClassifications({
              page: 1,
              pageSize: 10000,
              search,
              standard: form.getValues('standard') ? form.getValues('standard')?.id : undefined,
            });

            return response.results;
          }}
          getOptionLabel={(option) => getClassificationName(option)}
          isOptionEqualToValue={(option, value) => option.id === (!value ? undefined : value.id)}
          selectedOption={form.getValues('class')}
          setSelectedOption={(option) => {
            form.setValue('class', option, { shouldValidate: true, shouldDirty: true });
          }}
        />

        <AsyncAutocomplete<AuditorCategory>
          id="auditorCategory"
          label={t('Auditor category', { defaultValue: 'Auditor category' })}
          fullWidth
          disabled={!isFormEditable}
          getData={async (search) => {
            const response = await getAuditorCategories({
              page: 1,
              pageSize: 1000,
              search,
            });

            return response.results;
          }}
          getOptionLabel={(option) => option.name || 'N/A'}
          isOptionEqualToValue={(option, value) => option.id === (!value ? undefined : value.id)}
          selectedOption={form.getValues('auditorCategory')}
          setSelectedOption={(option) => {
            form.setValue('auditorCategory', option, { shouldValidate: true, shouldDirty: true });
          }}
        />
      </Box>

      {/* {isAdd && (
        <FormHelperText>
          {t(
            'Adding or removing registrations and competences will be available once the form is saved.',
            {
              defaultValue:
                'Adding or removing registrations and competences will be available once the form is saved.',
            }
          )}
        </FormHelperText>
      )} */}

      <Button
        variant="contained"
        type="button"
        onClick={() => {
          setIsModalOpen(true);
        }}
        fullWidth
        sx={{ mt: '1rem' }}
      >
        {t('Submit', { defaultValue: 'Submit' })}
      </Button>

      {!!form.formState.errors.root && (
        <FormHelperText error>{form.formState.errors.root.message}</FormHelperText>
      )}
    </Box>
  );
};

export default Form;
